import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import {
  ShoppingBag,
  LocalShipping,
  CheckCircle,
  Cancel,
  Visibility,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { orderAPI } from '../services/api';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Don't redirect while loading auth state
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchOrders();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await orderAPI.getMyOrders();
      console.log('Orders response:', response);

      const ordersList = response.data || response || [];
      setOrders(ordersList);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const upperStatus = status?.toUpperCase();
    switch (upperStatus) {
      case 'DELIVERED':
        return 'success';
      case 'SHIPPED':
        return 'info';
      case 'PLACED':
      case 'PROCESSING':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    const upperStatus = status?.toUpperCase();
    switch (upperStatus) {
      case 'DELIVERED':
        return <CheckCircle />;
      case 'SHIPPED':
        return <LocalShipping />;
      case 'CANCELLED':
        return <Cancel />;
      default:
        return <ShoppingBag />;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          pt: 12,
          pb: 8,
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        pt: { xs: 9, md: 10 },
        pb: 8,
        minHeight: '100vh',
        bgcolor: '#F8F6F1',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          className='font2'
          variant="h3"
          sx={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 700,
            mb: 1,
            textAlign: 'center',
          }}
        >
          My Orders
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, textAlign: 'center' }}
        >
          Track and manage your orders
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {orders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ShoppingBag sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No orders yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start shopping to see your orders here
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/shop')}
              sx={{
                borderRadius: 50,
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                },
              }}
            >
              Start Shopping
            </Button>
          </Box>
        ) : (
          <Stack spacing={3}>
            {orders.map((order) => (
              <Card
                key={order._id}
                sx={{
                  boxShadow: '0 4px 12px rgba(201, 169, 97, 0.15)',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(201, 169, 97, 0.25)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography className='font11' variant="h6" sx={{ fontWeight: 600, mr: 2 }}>
                          Order #{order.orderNumber}
                        </Typography>
                        <Chip
                          className='font03'
                          icon={getStatusIcon(order.status)}
                          label={order.status}
                          color={getStatusColor(order.status)}
                          size="small"
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Placed on: {new Date(order.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {order.items?.length || 0} item(s)
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Payment: {order.paymentMethod} - {order.paymentStatus}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      {order.items && order.items.length > 0 && (
                        <Stack spacing={1}>
                          {order.items.slice(0, 2).map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box
                                component="img"
                                src={item.productId?.images?.[0] || 'https://via.placeholder.com/50'}
                                alt={item.productId?.name || 'Product'}
                                sx={{
                                  width: 50,
                                  height: 50,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {item.productId?.name || 'Product'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.variantId?.weight} - {item.variantId?.packaging}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Qty: {item.quantity}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {order.currencySymbol || '$'}{item.displayPrice?.toFixed(2) || '0.00'}
                              </Typography>
                            </Box>
                          ))}
                          {order.items.length > 2 && (
                            <Typography variant="caption" color="text.secondary">
                              +{order.items.length - 2} more item(s)
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          bgcolor: 'background.paper',
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Order Total
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: 'primary.main',
                            mb: 2,
                          }}
                        >
                          {order.currencySymbol || '$'}{(order.displayFinalAmount).toFixed(2)}
                        </Typography>

                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<Visibility />}
                          onClick={() => navigate(`/orders/${order._id}`)}
                          sx={{
                            borderRadius: 50,
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              borderColor: 'primary.dark',
                              bgcolor: 'primary.light',
                            },
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default OrdersPage;
